import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { FormFeedback } from '@/components/ui/form-feedback';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { formatDate } from '@/lib/intl';

type Calendar =
  paths['/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/participant-calendar']['get']['responses'][200]['content']['application/json'];

type LoadState =
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'error' }
  | { status: 'ready'; calendar: Calendar };

/**
 * FR-9.29, CST-9.11, AC-9.16 (Resolution R4): renders ONLY `earliestAdmissibleDates` — the
 * `ParticipantCalendarResponseDto`'s single field, structurally incapable of carrying any
 * identifier, target date, or window (`schedule-access-response.dto.ts:76-81`). No merge with
 * the internal-schedule read anywhere in this component. No operator control besides the
 * `window.print()` trigger, itself hidden from the printout via `print:hidden`. The project's
 * first print-formatted rendering — `globals.css`'s new `@media print` block (Task 4.1) forces a
 * legible light-on-white printout regardless of the active theme.
 */
export function ParticipantCalendarPage() {
  const { t } = useTranslation('clinical-study');
  const { studyId, subjectId } = useParams<{ studyId: string; subjectId: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    if (studyId === undefined || subjectId === undefined) {
      return;
    }
    setState({ status: 'loading' });
    apiClient
      .GET('/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/participant-calendar', {
        params: { path: { studyId, subjectId } },
      })
      .then(({ data, error, response }) => {
        if (response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'error' });
          return;
        }
        setState({ status: 'ready', calendar: data });
      })
      .catch(() => setState({ status: 'error' }));
  }, [studyId, subjectId]);

  if (studyId === undefined || subjectId === undefined) {
    return null;
  }

  return (
    <div className="mx-auto max-w-xl space-y-6 print:max-w-none">
      <div className="flex items-center justify-between print:hidden">
        <Link to={`/clinical-study/studies/${studyId}`} className="text-sm underline">
          {t('participantCalendar.backToStudy')}
        </Link>
        <Button type="button" onClick={() => window.print()}>
          {t('participantCalendar.printButton')}
        </Button>
      </div>
      <h2 className="screen-title">{t('participantCalendar.title')}</h2>
      <p className="text-sm">{t('participantCalendar.intro')}</p>
      {/* `print:hidden` is kept from what it replaces: this screen exists to be printed, and a
          page of grey bones is not something to hand a participant. */}
      {state.status === 'loading' && (
        <SkeletonRegion className="space-y-2 print:hidden">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-4 w-44" />
        </SkeletonRegion>
      )}
      {state.status === 'forbidden' && (
        <FormFeedback tone="error">{t('participantCalendar.forbiddenMessage')}</FormFeedback>
      )}
      {state.status === 'error' && (
        <FormFeedback tone="error">{t('participantCalendar.loadError')}</FormFeedback>
      )}
      {state.status === 'ready' &&
        (state.calendar.earliestAdmissibleDates.length === 0 ? (
          <p className="text-sm">{t('participantCalendar.emptyMessage')}</p>
        ) : (
          <ol className="list-decimal space-y-1 pl-6 text-base">
            {state.calendar.earliestAdmissibleDates.map((date, index) => (
              <li key={`${date}-${index}`}>{formatDate(date)}</li>
            ))}
          </ol>
        ))}
    </div>
  );
}
